import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";

const faqs = [
  {
    q: "How fast will my shop be live?",
    a: "In under 7 days. Once your 50% payment is in and we have your menu and branding, we build your homepage, shop system and apps — you review, we launch.",
  },
  {
    q: "What if a feature I need is missing?",
    a: "Yes — we can develop it for you. simplyOwner is our own platform, so custom features, integrations and special workflows can be built on request. Tell us what you need and we will quote it individually.",
  },
  {
    q: "Do you provide support?",
    a: "Yes. We provide 24/7 support from Switzerland, in 7 languages. Real people, around the clock — included in your annual plan.",
  },
  {
    q: "Do I really pay zero commission?",
    a: "Really. 0% on every order, forever. You pay the one-time price and the small annual fee for hosting, domain and support. Nothing else — no matter how much you sell.",
  },
  {
    q: "What are the payment terms?",
    a: "Simple: 50% before we start the work (we send you a payment link), the remaining 50% only before launch. One price, no subscriptions.",
  },
  {
    q: "Do I own my customers and my data?",
    a: "100%. Your homepage, your domain, your apps, your customer database. If you ever leave, your data comes with you — that is what ownership means.",
  },
  {
    q: "Are the mobile apps really under my brand?",
    a: "Yes. Your iOS and Android apps are published under your restaurant's name and branding — your logo, your colors, your icon in the App Store and Google Play.",
  },
];

export default function FAQ() {
  const [open, setOpen] = useState(0);

  return (
    <section id="faq" data-testid="faq-section" className="max-w-7xl mx-auto px-6 lg:px-12 py-28 lg:py-40">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-32">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7 }}
              className="font-mono text-xs tracking-[0.3em] uppercase text-brand mb-6"
            >
              FAQ
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="font-display font-black uppercase tracking-tighter leading-[0.95] text-4xl sm:text-5xl lg:text-6xl text-ink"
            >
              Questions? <span className="block text-outline mt-2">Straight answers.</span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="mt-6 text-black/60 font-light text-base max-w-sm leading-relaxed"
            >
              24/7 support from Switzerland, in 7 languages. Something missing? We can develop it for you.
            </motion.p>
          </div>
        </div>

        <div className="lg:col-span-7">
          {faqs.map((f, i) => (
            <motion.div
              key={f.q}
              data-testid={`faq-item-${i + 1}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: 0.04 * i }}
              className="border-t border-black/10 last:border-b"
            >
              <button
                data-testid={`faq-question-${i + 1}`}
                onClick={() => setOpen(open === i ? -1 : i)}
                className="w-full flex items-center justify-between gap-6 py-7 text-left group"
              >
                <span className={`font-display font-bold text-lg lg:text-xl transition-colors duration-300 ${open === i ? "text-brand" : "text-ink group-hover:text-brand"}`}>
                  {f.q}
                </span>
                <span className={`w-9 h-9 rounded-full border flex items-center justify-center shrink-0 transition-[transform,border-color,background-color] duration-300 ${open === i ? "rotate-45 border-brand bg-brand text-white" : "border-black/15 text-ink/50"}`}>
                  <Plus className="w-4 h-4" />
                </span>
              </button>
              <AnimatePresence initial={false}>
                {open === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                    className="overflow-hidden"
                  >
                    <p data-testid={`faq-answer-${i + 1}`} className="pb-7 pr-14 text-black/60 font-light leading-relaxed text-base">
                      {f.a}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
